import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, ArrowRight, LayoutDashboard, ReceiptText } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import ClinicPicker from '../../components/ClinicPicker';
import ClinicDetailsCard from '../../components/ClinicDetailsCard';
import * as ordersApi from '../../api/orders.api';
import { formatMoney, formatDateTime } from '../../utils/format';
import { trackPurchase } from '../../utils/analytics';

// Where Curlec redirects the user after they complete (or abandon) payment.
// Query params sent by Curlec (Razorpay Payment Link callback):
//   orderNumber                         (our own — set in PAYMENT_REDIRECT_URL)
//   razorpay_payment_id                 e.g. pay_TKLUnavTYQgHFc
//   razorpay_payment_link_id            e.g. plink_TKLT4eG0TPDTG1
//   razorpay_payment_link_reference_id  our orderNumber
//   razorpay_payment_link_status        paid | cancelled | expired
//   razorpay_signature                  HMAC of the above with key_secret
//
// This page is a THIN status view — it never mutates state. The webhook has
// already been (or will shortly be) verified server-side and flipped the
// order's paymentStatus. We just look up the current DB state and display it.

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 8;   // 8 × 2.5s = 20s ceiling waiting for webhook

function findOrder(orders, orderNumber) {
  if (!orderNumber || !Array.isArray(orders)) return null;
  return orders.find((o) => o.orderNumber === orderNumber) || null;
}

function PaymentReturn() {
  const [params] = useSearchParams();
  const orderNumber = params.get('orderNumber')
                   || params.get('razorpay_payment_link_reference_id')
                   || '';
  const linkStatus = (params.get('razorpay_payment_link_status') || '').toLowerCase();
  const paymentId = params.get('razorpay_payment_id') || '';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // When true, treat the order as "unassigned" locally so the picker
  // reopens even though the DB still has assignedVendor set. The next
  // POST /orders/:id/assign-clinic overwrites the assignment atomically.
  const [changingClinic, setChangingClinic] = useState(false);
  const attemptRef = useRef(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;

    async function fetchOnce() {
      const list = await ordersApi.myOrders().catch(() => []);
      const found = findOrder(list, orderNumber);
      if (cancelRef.current) return;
      setOrder(found);
      setLoading(false);
      return found;
    }

    async function pollUntilResolved() {
      try {
        let found = await fetchOnce();

        // If Curlec says the payment link is `paid` but the webhook hasn't
        // updated the order yet, poll a few times before giving up — the
        // browser redirect and the webhook race each other.
        while (
          !cancelRef.current
          && found
          && linkStatus === 'paid'
          && found.paymentStatus === 'PENDING'
          && attemptRef.current < POLL_MAX_ATTEMPTS
        ) {
          attemptRef.current += 1;
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          found = await fetchOnce();
        }
      } catch (e) {
        if (cancelRef.current) return;
        setErr((e && e.message) || 'Could not fetch order status.');
        setLoading(false);
      }
    }

    if (!orderNumber) {
      setErr('Missing order reference in the return URL.');
      setLoading(false);
      return () => { cancelRef.current = true; };
    }

    pollUntilResolved();
    return () => { cancelRef.current = true; };
  }, [orderNumber, linkStatus]);

  // Resolve display state from what we actually know:
  //   1. Curlec's redirect status (fast, but never trusted for money)
  //   2. Our own Order.paymentStatus (verified webhook truth)
  const resolved = resolveDisplayState({ order, linkStatus, err, loading });

  // Fire the GA4 `purchase` event exactly once per order when we've
  // confirmed PAID via our own webhook state (never based on the URL
  // param alone — Razorpay's `paid` in the URL can arrive before money
  // has actually cleared).
  const purchaseFiredRef = useRef(false);
  useEffect(() => {
    if (purchaseFiredRef.current) return;
    if (order && order.paymentStatus === 'PAID') {
      purchaseFiredRef.current = true;
      trackPurchase({
        transaction_id: paymentId || order.orderNumber,
        value: Number(order.amount) || 1599.00,
        currency: order.currency || 'MYR',
        package_code: order.packageSnapshot?.name || undefined,
      });
    }
  }, [order, paymentId]);

  return (
    <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
      <div className="card" style={{ padding: 32 }}>
        <StatusIcon state={resolved.state} />
        <h2 style={{ marginTop: 20 }}>{resolved.title}</h2>
        <p className="muted" style={{ marginTop: 8 }}>{resolved.body}</p>

        {order && (
          <dl className="kvs" style={{ marginTop: 24, textAlign: 'left', gridTemplateColumns: '1fr auto' }}>
            <dt>Order</dt>
            <dd className="cell-mono">{order.orderNumber}</dd>
            <dt>Amount</dt>
            <dd className="bold">{formatMoney(order.amount, order.currency || 'MYR')}</dd>
            <dt>Payment</dt>
            <dd>{order.paymentStatus}</dd>
            {order.createdAt && (
              <>
                <dt>Created</dt>
                <dd>{formatDateTime(order.createdAt)}</dd>
              </>
            )}
            {paymentId && (
              <>
                <dt>Gateway ref</dt>
                <dd className="cell-mono" style={{ wordBreak: 'break-all' }}>{paymentId}</dd>
              </>
            )}
          </dl>
        )}

        {/* Client 2026-09-08: after PAID, patient picks their own clinic
            from a list sorted by their current GPS location. Once picked,
            the clinic details card replaces the picker.
            Client 2026-09-09: patient can tap "Change clinic" on the
            details card to reopen the picker and switch. */}
        {order?.paymentStatus === 'PAID'
          && (!order.assignedVendor || changingClinic) && (
          <ClinicPicker
            orderId={order._id}
            onPicked={(updated) => { setOrder(updated); setChangingClinic(false); }}
          />
        )}
        {order?.paymentStatus === 'PAID' && order.assignedVendor && !changingClinic && (
          <ClinicDetailsCard
            clinic={order.assignedVendor}
            onChangeClinic={() => setChangingClinic(true)}
          />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
          {order?._id && (
            <Link to={`/orders/${order._id}`} className="btn btn-primary">
              <ReceiptText size={16} /> View order
            </Link>
          )}
          <Link to="/dashboard" className="btn btn-ghost">
            <LayoutDashboard size={16} /> Dashboard
          </Link>
          {resolved.state === 'failed' && (
            <Link to="/pay-now" className="btn btn-outline">
              Try again <ArrowRight size={16} />
            </Link>
          )}
        </div>

        {loading && resolved.state === 'pending' && (
          <p className="text-xs muted" style={{ marginTop: 18 }}>
            <LoadingSpinner /> Finalising your booking…
          </p>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ state }) {
  const style = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%' };
  if (state === 'paid') {
    return <div style={{ ...style, background: 'var(--brand-50, #ecfeff)', color: 'var(--brand-700, #0e7490)' }}>
      <CheckCircle2 size={32} />
    </div>;
  }
  if (state === 'failed') {
    return <div style={{ ...style, background: 'var(--rose-50, #fef1f2)', color: 'var(--rose-600, #e11d48)' }}>
      <XCircle size={32} />
    </div>;
  }
  return <div style={{ ...style, background: 'var(--amber-50, #fffbeb)', color: 'var(--amber-600, #d97706)' }}>
    <Clock size={32} />
  </div>;
}

// Map { order, linkStatus, err, loading } → { state, title, body }.
function resolveDisplayState({ order, linkStatus, err, loading }) {
  if (err) {
    return {
      state: 'failed',
      title: 'We couldn\'t confirm your payment',
      body: err + ' Please check your orders — your payment may still have gone through.',
    };
  }
  if (loading) {
    return {
      state: 'pending',
      title: 'Confirming your payment…',
      body: 'Just a moment — we\'re waiting for the payment to reach us.',
    };
  }
  if (!order) {
    return {
      state: 'failed',
      title: 'Order not found',
      body: 'We couldn\'t locate the order this payment relates to. If you were charged, contact support with your payment ID.',
    };
  }
  if (order.paymentStatus === 'PAID') {
    // Once assigned, the clinic card is rendered inline — surface it in
    // the copy instead of the vague "we'll be in touch" line.
    return {
      state: 'paid',
      title: 'Payment successful',
      body: order.assignedVendor
        ? 'Thank you! Your booking is confirmed. Your clinic is shown below — schedule your appointment when you\'re ready.'
        : 'Thank you! Your booking is confirmed. Pick your preferred clinic below to continue.',
    };
  }
  if (order.paymentStatus === 'FAILED' || linkStatus === 'expired' || linkStatus === 'cancelled') {
    return {
      state: 'failed',
      title: linkStatus === 'cancelled' ? 'Payment cancelled' : 'Payment did not go through',
      body: 'No charge was made. You can try again below.',
    };
  }
  // linkStatus 'paid' but our webhook hasn't landed yet — treat as pending.
  return {
    state: 'pending',
    title: 'Payment received',
    body: 'Thanks! Your payment has come through and we\'re just finalising your booking. This page will update automatically in a few seconds — feel free to close it and check your orders any time.',
  };
}

export default PaymentReturn;
